import React from 'react';

import { listingSourcesApi } from '../../requests/listingSources';
import RunDetail from './RunDetail';
import RunOverview from './RunOverview';
import SettingsModal from './SettingsModal';
import { formatNumber, zoneAbbreviation } from './format';

const LISTINGS_PER_PAGE = 50;

// A run in progress moves; nothing else on the screen does. Polling only while
// one is going keeps a screen somebody left open from asking all day.
const POLL_MS = 5000;

/**
 * Where the listings a crawler writes into PROPERTIES can be accounted for.
 *
 * It answers three questions and nothing else: what is this set to do, what
 * did it do, and what did it meet that it could not translate. Nothing here
 * names a vendor — the sources come from what the server has registered.
 */
export default class ListingSources extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            sources: null,
            active: 0,
            loading: true,
            error: null,

            openRun: null,
            settingsOpen: false,
            notice: null,
            queueing: null,
            refreshToken: 0,
            refreshedAt: null,
        };

        this.saveSettings = this.saveSettings.bind(this);
        this.openSettings = this.openSettings.bind(this);
        this.closeSettings = this.closeSettings.bind(this);
        this.openRun = this.openRun.bind(this);
        this.backToRuns = this.backToRuns.bind(this);
        this.runNow = this.runNow.bind(this);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.pollTimer);
    }

    load() {
        listingSourcesApi.sources().then(
            (data) => {
                if (this.unmounted) return;
                this.setState(
                    { sources: data.sources, loading: false, refreshedAt: Date.now() },
                    () => this.schedulePoll()
                );
            },
            (error) => {
                if (this.unmounted) return;
                this.setState({
                    loading: false,
                    error: error.message || 'The listing sources could not be loaded.',
                });
            }
        );
    }

    /** Re-ask only while something is actually moving. */
    schedulePoll() {
        clearTimeout(this.pollTimer);

        const source = this.source();
        if (!source || !source.running) return;

        this.pollTimer = setTimeout(() => {
            listingSourcesApi.sources().then(
                (data) => {
                    if (this.unmounted) return;

                    const stillRunning = data.sources[this.state.active]
                        && data.sources[this.state.active].running;

                    this.setState(
                        (state) => ({
                            sources: data.sources,
                            refreshedAt: Date.now(),
                            // A run that has just finished changes the history
                            // below it, so the list is told to reload once.
                            refreshToken: stillRunning ? state.refreshToken : state.refreshToken + 1,
                        }),
                        () => this.schedulePoll()
                    );
                },
                () => {}
            );
        }, POLL_MS);
    }

    source() {
        const { sources, active } = this.state;
        return sources ? sources[active] : null;
    }

    openSettings() {
        this.setState({ settingsOpen: true });
    }

    closeSettings() {
        this.setState({ settingsOpen: false });
    }

    openRun(run) {
        this.setState({ openRun: run });
    }

    backToRuns() {
        this.setState({ openRun: null, refreshToken: this.state.refreshToken + 1 });
    }

    saveSettings(settings) {
        const source = this.source();

        return listingSourcesApi.saveSettings(source.key, settings).then((data) => {
            const sources = this.state.sources.slice();
            sources[this.state.active] = Object.assign({}, source, { settings: data.settings });
            this.setState({ sources });
            return data;
        });
    }

    runNow(mode) {
        const source = this.source();
        this.setState({ queueing: mode, notice: null });

        listingSourcesApi.runNow(source.key, mode).then(
            (data) => {
                if (this.unmounted) return;

                this.setState({
                    queueing: null,
                    notice: data.queued
                        ? {
                            kind: 'ok',
                            text: mode === 'dry_run'
                                ? 'Dry run queued — nothing will be written.'
                                : 'Queued — starting within a minute.',
                        }
                        // Not a success and not an error: the button did
                        // nothing, and saying "queued" is how somebody ends up
                        // pressing it again.
                        : { kind: 'warn', text: data.reason || 'A run is already going; this one was not queued.' },
                });

                this.load();
            },
            (error) => {
                if (this.unmounted) return;
                this.setState({
                    queueing: null,
                    notice: { kind: 'error', text: error.message || 'Could not start a run.' },
                });
            }
        );
    }

    renderHeader() {
        const source = this.source();
        const settings = source.settings;
        const ceiling = (settings.max_pages || 1) * LISTINGS_PER_PAGE;
        const running = source.running;
        const busy = running || this.state.queueing !== null;

        const schedule = (settings.enabled
            ? 'Nightly ' + settings.run_at + ' ' + zoneAbbreviation(settings.timezone)
            : 'No nightly run') +
            ' · ' + (settings.states || []).join(' ') +
            ' · ' + (settings.searches || []).length + ' searches' +
            // Per search and state, not per run: each is asked for separately.
            ' · ≤ ' + formatNumber(ceiling) + ' listings each';

        return (
            <div className="ls-header">
                <div className="ls-header__headings">
                    <h1 className="ls-header__title">{source.label}</h1>
                    <div className="ls-header__meta">
                        <span className={'ls-pill ls-pill--schedule' + (settings.enabled ? ' is-on' : '')}>
                            {settings.enabled ? 'Schedule on' : 'Schedule off'}
                        </span>
                        <span className="ls-header__schedule">{schedule}</span>
                    </div>
                </div>

                <div className="ls-header__actions">
                    <button type="button" className="ls-btn" onClick={this.openSettings}>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor"
                            strokeWidth="1.4" aria-hidden="true">
                            <circle cx="7.5" cy="7.5" r="2.2" />
                            <path d="M7.5 1.6 V3.2 M7.5 11.8 V13.4 M1.6 7.5 H3.2 M11.8 7.5 H13.4 M3.4 3.4 L4.5 4.5 M10.5 10.5 L11.6 11.6 M11.6 3.4 L10.5 4.5 M4.5 10.5 L3.4 11.6" />
                        </svg>
                        Settings
                    </button>
                    <button type="button" className="ls-btn" disabled={busy} onClick={() => this.runNow('dry_run')}>
                        {this.state.queueing === 'dry_run' ? 'Queueing…' : 'Dry run'}
                    </button>
                    <button type="button" className="ls-btn ls-btn--primary" disabled={busy}
                        onClick={() => this.runNow('write')}>
                        {running ? 'Run in progress' : this.state.queueing === 'write' ? 'Queueing…' : 'Run now'}
                    </button>
                </div>
            </div>
        );
    }

    renderBlocked(title, text) {
        return (
            <div className="ListingSources">
                <div className="ls-blockedwrap">
                    <div className="ls-blocked">
                        <div className="ls-blocked__mark" />
                        <div className="ls-blocked__title">{title}</div>
                        <p className="ls-blocked__text">{text}</p>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loading, error, sources, openRun, settingsOpen, notice } = this.state;

        if (loading) {
            return (
                <div className="ListingSources">
                    <div className="ls-container"><div className="ls-empty">Loading listing sources…</div></div>
                </div>
            );
        }

        if (error) {
            return this.renderBlocked('Nothing to show', error);
        }

        if (!sources || sources.length === 0) {
            return this.renderBlocked(
                'No sources registered',
                <span>
                    Nothing is set up to pull listings yet. A source is registered in
                    <span className="ls-mono"> config/listing-sources.php</span>.
                </span>
            );
        }

        const source = this.source();

        return (
            <div className="ListingSources">
                <div className="ls-container">
                    {sources.length > 1 && (
                        <div className="ls-sourcetabs">
                            {sources.map((entry, index) => (
                                <button
                                    key={entry.key}
                                    type="button"
                                    className={'ls-sourcetab' + (index === this.state.active ? ' is-active' : '')}
                                    onClick={() => this.setState({ active: index, openRun: null })}
                                >
                                    {entry.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {!openRun && this.renderHeader()}

                    {notice && (
                        <div className={'ls-alert ls-alert--' + notice.kind}>
                            <span className="ls-alert__text">{notice.text}</span>
                            <button type="button" className="ls-alert__dismiss" aria-label="Dismiss"
                                onClick={() => this.setState({ notice: null })}>
                                ×
                            </button>
                        </div>
                    )}

                    {openRun ? (
                        <RunDetail source={source} run={openRun} onBack={this.backToRuns} />
                    ) : (
                        <RunOverview
                            source={source}
                            refreshToken={this.state.refreshToken}
                            refreshedAt={this.state.refreshedAt}
                            onOpenRun={this.openRun}
                            onOpenSettings={this.openSettings}
                            onRunNow={this.runNow}
                        />
                    )}
                </div>

                {settingsOpen && (
                    <SettingsModal
                        source={source}
                        settings={source.settings}
                        onSave={this.saveSettings}
                        onClose={this.closeSettings}
                    />
                )}
            </div>
        );
    }
}
